class ProductGrouping extends HTMLElement {
  constructor() {
    super();
    this.debug = false; // Set to true for debugging
    this.parentComponent = this;
    this.sectionId = this.getAttribute('data-section-id');
    this.optionsToRequestData = this.querySelectorAll('[js-request-new-product]');
    this.buttonsToUpdateSubOptions = this.querySelectorAll('[js-update-sub-option]');
    this.buttonsToUpdateVariant = this.querySelectorAll('[js-update-variant]');

    this.defaultVariantId = this.getAttribute('data-default-variant-id');
    this.currentProductId = this.getAttribute('data-current-product-id');
    this.currentVariantId = this._getVariantFromURL() || this.defaultVariantId;
    this.currentVariantAvailable = this.getAttribute('data-default-variant-available') === 'true';
    this.currentProductHandle = this.getAttribute('data-current-product-handle');
    this.currentOptions = {
      option2: this.getAttribute('data-current-option2'),
      option3: this.getAttribute('data-current-option3'),
    };

    this.isQuickView = this.dataset.isQuickView === 'true' || Boolean(this.closest('quick-view-modal'));

    if (this.isQuickView) {
      if (this.debug) console.log('ProductGrouping: quick-view mode enabled');
      //this.querySelectorAll('.product-grouping__option__swatches').forEach(el => el.style.display = 'none');
    }

    this.productGroupingData = {};
    this.dataProductGroup = JSON.parse(this.querySelector("script[data-product-group]")?.textContent || "{}");

    // Prevent custom.js from interfering with our button state management
    this._preventVariantChangeInterference();

    this._checkAndSetDefaultVariant();
    this._bindEvents();
    this._setupSizeGuide();
  }

  _preventVariantChangeInterference() {
    // Listen for variant:change events and stop propagation to prevent custom.js interference
    document.addEventListener('variant:change', (event) => {
      if (this.debug) console.log('[ProductGrouping]: Intercepting variant:change event to prevent custom.js interference');
      // Stop the event from reaching the custom.js listener
      event.stopImmediatePropagation();
    }, { capture: true }); // Use capture phase to catch it before custom.js
  }

  _bindEvents() {
    this.optionsToRequestData.forEach(option => {
      option.addEventListener('click', (event) => this._handleOptionChange(event, option));
    });

    this._bindVariantButtons();
    if (this.debug) console.log('Bound initial variant buttons:', this.buttonsToUpdateVariant.length);
  }

  _bindVariantButtons() {
    this.buttonsToUpdateVariant.forEach(button => {
      // Store the bound handler for later removal
      const handler = (event) => this._handleUpdateVariant(event, button);
      button._variantClickHandler = handler;
      button.addEventListener('click', handler);
    });
  }

  _rebindVariantButtons() {
    this.buttonsToUpdateVariant.forEach(button => {
      if (button._variantClickHandler) {
        button.removeEventListener('click', button._variantClickHandler);
        button._variantClickHandler = null;
      }
    });
    this.buttonsToUpdateVariant = this.querySelectorAll('[js-update-variant]');
    if (this.debug) console.log('Rebinding variant buttons:', this.buttonsToUpdateVariant.length);
    this._bindVariantButtons();
  }

  _checkAndSetDefaultVariant() {
    if (!this.currentVariantId && this.defaultVariantId) {
      this._updateURLWithVariant(this.defaultVariantId);
      this.currentVariantId = this.defaultVariantId;
    }

    const scriptTagsData = this.querySelectorAll(
      "script[js-product-group-data]"
    );
    this.productGroupingData = Array.from(scriptTagsData).reduce(
      (acc, script) => {
        const data = JSON.parse(script.textContent || "{}");
        const key = script.getAttribute("data-product-id");
        if (!acc[key]) {
          acc[key] = { tag_options: [], variants: [] };
        }
        const existingData = acc[key];
        existingData.tag_options.push(...data.tag_options);
        const uniqueVariants = data.variants.filter(
          (newVariant) =>
            !existingData.variants.some(
              (existingVariant) => existingVariant.id === newVariant.id
            )
        );
        existingData.variants.push(...uniqueVariants);
        return acc;
      },
      {}
    );

    // Check real-time availability for the current variant on page load
    if (this.currentVariantId) {
      this._fetchVariantAvailability(this.currentVariantId)
        .then((realTimeAvailable) => {
          console.log(
            `[ProductGrouping]: Initial variant ID ${this.currentVariantId} real-time available: ${realTimeAvailable}`
          );

          const addToCartForm = document.querySelector(
            ".product-info .shopify-product-form"
          );
          const addToCartButton = addToCartForm?.querySelector(
            'buy-buttons button[type="submit"]'
          );
          const notifyMeBtn = addToCartForm?.querySelector(
            "buy-buttons button[data-bis-trigger]"
          );
          const addToCartButtonText = addToCartButton?.querySelector("div");
          const soldOutElement =
            this.closest(".product-info")?.querySelector("sold-out-badge");

          if (
            !realTimeAvailable &&
            addToCartButton &&
            notifyMeBtn &&
            addToCartButtonText
          ) {
            console.log(
              `[ProductGrouping]: Initial variant is sold out, showing Join Waitlist`
            );
            this.showJoinWaitListButton(
              addToCartButton,
              notifyMeBtn,
              addToCartButtonText,
              soldOutElement
            );

            const $submit = document.querySelectorAll(
              "product-quick-add .buy-buttons__submit"
            );
            $submit.forEach((submit) => {
              submit.addEventListener("click", this._scrollToBuyButtons);
            });
          }
        })
        .catch((error) => {
          console.error(
            "[ProductGrouping] Failed to fetch initial variant availability:",
            error
          );

          // Fallback to original logic if API fails
          if (!this.currentVariantAvailable) {
            const addToCartForm = document.querySelector(
              ".product-info .shopify-product-form"
            );
            const addToCartButton = addToCartForm?.querySelector(
              'buy-buttons button[type="submit"]'
            );
            const notifyMeBtn = addToCartForm?.querySelector(
              "buy-buttons button[data-bis-trigger]"
            );
            const addToCartButtonText = addToCartButton?.querySelector("div");
            const soldOutElement =
              this.closest(".product-info")?.querySelector("sold-out-badge");
            if (addToCartButton && notifyMeBtn && addToCartButtonText) {
              this.showJoinWaitListButton(
                addToCartButton,
                notifyMeBtn,
                addToCartButtonText,
                soldOutElement
              );
            }

            const $submit = document.querySelectorAll(
              "product-quick-add .buy-buttons__submit"
            );
            $submit.forEach((submit) => {
              submit.addEventListener("click", this._scrollToBuyButtons);
            });
          }
        });
    } else if (!this.currentVariantAvailable) {
      // Original logic for variants that Shopify already knows are unavailable
      const addToCartForm = document.querySelector(
        ".product-info .shopify-product-form"
      );
      const addToCartButton = addToCartForm?.querySelector(
        'buy-buttons button[type="submit"]'
      );
      const notifyMeBtn = addToCartForm?.querySelector(
        "buy-buttons button[data-bis-trigger]"
      );
      const addToCartButtonText = addToCartButton?.querySelector("div");
      const soldOutElement =
        this.closest(".product-info")?.querySelector("sold-out-badge");
      if (addToCartButton && notifyMeBtn && addToCartButtonText) {
        this.showJoinWaitListButton(
          addToCartButton,
          notifyMeBtn,
          addToCartButtonText,
          soldOutElement
        );
      }

      const $submit = document.querySelectorAll(
        "product-quick-add .buy-buttons__submit"
      );
      $submit.forEach((submit) => {
        submit.addEventListener("click", this._scrollToBuyButtons);
      });
    }

    // ✅ Force update the stock message on load
    const stockMessageEl = document.querySelector(
      'product-stock-message[data-context="pdp"]'
    );
    if (stockMessageEl && this.currentVariantId) {
      //console.log('[ProductGrouping] Initializing stock message for PDP with variant:', this.currentVariantId);
      stockMessageEl.updateVariantId(this.currentVariantId);
    }
  }
 
  _getVariantFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('variant');
  }

  _updateURLWithVariant(variantId) {
    if (this.isQuickView || !variantId) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url.toString());
  }

  _handleUpdateVariant(event, button) {
    event.preventDefault();
    const newVariantId = button.dataset.variantId;
    const newVariantPrice = button.dataset.variantPrice;
    const isAvailable = button.dataset.available === 'true';
    const optionIndex = button.dataset.optionIndex;
    const newValue = button.dataset.value;

    if (this.debug) console.log('Handling variant update:', { newVariantId, newVariantPrice, isAvailable, optionIndex, newValue });

    if (!newVariantId || !optionIndex || !newValue) {
      console.error("Missing required button dataset attributes:", {
        newVariantId,
        optionIndex,
        newValue,
      });
      return;
    }

    const addToCartForm = document.querySelector(
      ".product-info .shopify-product-form"
    );
    const input = addToCartForm?.querySelector('input[name="id"]');
    const soldOutElement =
      this.closest(".product-info")?.querySelector("sold-out-badge");

    if (!addToCartForm || !input) {
      console.error("Add to cart form or input not found:", {
        addToCartForm: !!addToCartForm,
        input: !!input,
      });
      return;
    }

    this.currentOptions[`option${optionIndex}`] = newValue;
    this.currentVariantId = newVariantId;
    input.value = newVariantId;
    this.updateVariantClass();
    this._updateURLWithVariant(newVariantId);

    // Notify AMP Back in Stock app about variant change
    this._notifyAmpBackInStock({
      id: newVariantId,
      available: isAvailable,
      price: newVariantPrice,
    });

    const stockMessages = [
      ...this.parentComponent.querySelectorAll("product-stock-message"),
      ...(this.parentComponent
        .closest("quick-view-modal")
        ?.querySelectorAll("product-stock-message") || []),
    ];
    stockMessages.forEach((stockMessage) => {
      stockMessage.updateVariantId(newVariantId);
    });

    const addToCartButton = addToCartForm?.querySelector(
      'buy-buttons button[type="submit"]'
    );
    const notifyMeBtn = addToCartForm?.querySelector(
      "buy-buttons button[data-bis-trigger]"
    );
    const addToCartButtonText = addToCartButton?.querySelector("div");

    // Fetch real-time variant availability to ensure consistency
    this._fetchVariantAvailability(newVariantId)
      .then((realTimeAvailable) => {
        console.log(
          `[ProductGrouping]: Variant ID ${newVariantId} real-time available: ${realTimeAvailable}`
        );
        console.log(`[ProductGrouping]: Button elements found:`, {
          addToCartButton: !!addToCartButton,
          notifyMeBtn: !!notifyMeBtn,
          addToCartButtonText: !!addToCartButtonText,
          soldOutElement: !!soldOutElement,
        });

        if (realTimeAvailable) {
          console.log(
            `[ProductGrouping]: Showing Add to Bag for variant ${newVariantId}`
          );
          const isPreorder = document
            .querySelector("#main-product-info")
            ?.classList.contains("preorder");
          if (addToCartButton) {
            addToCartButton.removeAttribute("disabled");
            addToCartButton.classList.remove("button--subdued");
          }
          const num =
            parseFloat((newVariantPrice || "").replace(/[^0-9.]/g, "")) || 0;
          const formatted = num.toLocaleString(undefined, {
            style: "currency",
            currency: "USD",
          });

          if (addToCartButtonText) {
            addToCartButtonText.textContent = isPreorder
              ? "PREORDER"
              : `Add to Bag`;
          }

          if (addToCartButton) addToCartButton.style.display = "block";
          if (notifyMeBtn) notifyMeBtn.style.display = "none";
          if (soldOutElement) soldOutElement.classList.add("is-hidden");
        } else if (addToCartButton && notifyMeBtn && addToCartButtonText) {
          console.log(
            `[ProductGrouping]: Showing Join Waitlist for variant ${newVariantId}`
          );
          this.showJoinWaitListButton(
            addToCartButton,
            notifyMeBtn,
            addToCartButtonText,
            soldOutElement
          );
        } else {
          console.error(
            `[ProductGrouping]: Missing required elements for waitlist button:`,
            {
              addToCartButton: !!addToCartButton,
              notifyMeBtn: !!notifyMeBtn,
              addToCartButtonText: !!addToCartButtonText,
              soldOutElement: !!soldOutElement,
            }
          );
        }

        this.updateQuickAddCTA(
          realTimeAvailable,
          newVariantId,
          newVariantPrice
        );
      })
      .catch((error) => {
        console.error(
          "[ProductGrouping] Failed to fetch real-time variant availability, falling back to cached data:",
          error
        );

        // Fallback to original cached logic if API fails
        if (isAvailable) {
          const isPreorder = document
            .querySelector("#main-product-info")
            ?.classList.contains("preorder");
          if (addToCartButton) {
            addToCartButton.removeAttribute("disabled");
            addToCartButton.classList.remove("button--subdued");
          }
          const num =
            parseFloat((newVariantPrice || "").replace(/[^0-9.]/g, "")) || 0;
          const formatted = num.toLocaleString(undefined, {
            style: "currency",
            currency: "USD",
          });

          if (addToCartButtonText) {
            addToCartButtonText.textContent = isPreorder
              ? "PREORDER"
              : `Add to Bag`;
          }

          if (addToCartButton) addToCartButton.style.display = "block";
          if (notifyMeBtn) notifyMeBtn.style.display = "none";
          if (soldOutElement) soldOutElement.classList.add("is-hidden");
        } else if (addToCartButton && notifyMeBtn && addToCartButtonText) {
          this.showJoinWaitListButton(
            addToCartButton,
            notifyMeBtn,
            addToCartButtonText,
            soldOutElement
          );
        }

        this.updateQuickAddCTA(isAvailable, newVariantId, newVariantPrice);
      });
  }

  updateQuickAddCTA(isAvailable, newVariantId, newVariantPrice) {
    document.querySelectorAll('product-quick-add').forEach(quickAdd => {
      const $submit = quickAdd.querySelector('.buy-buttons__submit');
      const input = quickAdd.querySelector('input[name="id"]');
      if (input) input.value = newVariantId;

      if (isAvailable && $submit) {
        const isPreorder = document.querySelector("#main-product-info")?.classList.contains("preorder");
        $submit.querySelector("div").innerHTML = isPreorder
          ? `PREORDER - ${newVariantPrice}`
          : `Add to Bag - ${newVariantPrice}`;
        $submit.setAttribute("type", "submit");
        $submit.removeEventListener("click", this._scrollToBuyButtons);
      } else if ($submit) {
        $submit.querySelector('div').innerHTML = 'Join Waitlist';
        $submit.setAttribute('type', 'button');
        $submit.addEventListener('click', this._scrollToBuyButtons);
      }
    });
  }

  _scrollToBuyButtons(event) {
    event.preventDefault();
    const scrollToElement = document.querySelector('.product-info__buy-buttons');
    if (scrollToElement) {
      scrollToElement.scrollIntoView({ behavior: 'smooth' });
    }
  }

  updateVariantClass() {
    Object.values(this.currentOptions).forEach((data, index) => {
      const optionIndex = index + 2;
      const row = this.querySelector(`.product-grouping__options[data-options-row="${optionIndex}"]`);
      if (optionIndex === 2) {
        row?.querySelectorAll('button[js-update-variant]').forEach(button => {
          const isVisible = button.dataset.option3 === this.currentOptions.option3;
          const isActive = button.dataset.value === this.currentOptions[`option${optionIndex}`];
          button.classList.toggle('is-active', isActive);
          button.classList.toggle('is-hidden', !isVisible);
        });
      }
      if (optionIndex === 3) {
        row?.querySelectorAll('button[js-update-variant]').forEach(button => {
          const isVisible = button.dataset.option2 === this.currentOptions.option2;
          const isActive = button.dataset.value === this.currentOptions[`option${optionIndex}`];
          button.classList.toggle('is-active', isActive);
          button.classList.toggle('is-hidden', !isVisible);
        });
      }
    });
  }

  showJoinWaitListButton(addToCartButton, notifyMeBtn, addToCartButtonText, soldOutElement) {
    console.log(
      "[ProductGrouping]: showJoinWaitListButton called with elements:",
      {
        addToCartButton: !!addToCartButton,
        notifyMeBtn: !!notifyMeBtn,
        addToCartButtonText: !!addToCartButtonText,
        soldOutElement: !!soldOutElement,
      }
    );

    if (addToCartButton) {
      console.log("[ProductGrouping]: Hiding addToCartButton");
      addToCartButton.style.display = "none";
    }
    if (addToCartButtonText) {
      console.log('[ProductGrouping]: Setting button text to "Sold Out"');
      addToCartButtonText.textContent = "Sold Out";
    }
    if (notifyMeBtn) {
      console.log("[ProductGrouping]: Showing notifyMeBtn (Join Waitlist)");
      notifyMeBtn.style.display = "block";
    }
    if (soldOutElement) {
      console.log("[ProductGrouping]: Showing soldOutElement");
      soldOutElement.classList.remove("is-hidden");
    }
  }

  async _fetchVariantStock(variantId) {
    const accessToken = window.__VPV?.shop?.storefrontToken;
    const store = window.__VPV?.shop?.name;
    const locale = window.__VPV?.shop?.locale || 'en-US';

    if (!accessToken || !store) {
      console.error('[ProductGrouping] Missing Shopify token or store name');
      return 0;
    }

    const query = `
      query GetVariantStock($variantId: ID!) {
        node(id: $variantId) {
          ... on ProductVariant {
            id
            quantityAvailable
          }
        }
      }
    `;

    const variables = { variantId: `gid://shopify/ProductVariant/${variantId}` };

    try {
      const response = await fetch(`https://${store}.myshopify.com/api/2025-01/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': accessToken,
          'Accept': 'application/json',
          'Accept-Language': locale,
        },
        body: JSON.stringify({ query, variables }),
      });

      if (!response.ok) {
        console.error(`[ProductGrouping] Shopify API error: ${response.status}`, await response.text());
        return 0;
      }

      const result = await response.json();
      const qty = result?.data?.node?.quantityAvailable ?? 0;
      return qty;
    } catch (error) {
      console.error('[ProductGrouping] Error fetching stock quantity:', error);
      return 0;
    }
  }

  async _fetchVariantAvailability(variantId) {
    const accessToken = window.__VPV?.shop?.storefrontToken;
    const store = window.__VPV?.shop?.name;
    const locale = window.__VPV?.shop?.locale || 'en-US';

    if (!accessToken || !store) {
      console.error('[ProductGrouping] Missing Shopify token or store name for availability check');
      return false;
    }

    const query = `
      query GetVariantAvailability($variantId: ID!) {
        node(id: $variantId) {
          ... on ProductVariant {
            id
            availableForSale
          }
        }
      }
    `;

    const variables = { variantId: `gid://shopify/ProductVariant/${variantId}` };

    try {
      const response = await fetch(`https://${store}.myshopify.com/api/2025-01/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': accessToken,
          'Accept': 'application/json',
          'Accept-Language': locale,
        },
        body: JSON.stringify({ query, variables }),
      });

      if (!response.ok) {
        console.error(`[ProductGrouping] Shopify API error for availability: ${response.status}`, await response.text());
        return false;
      }

      const result = await response.json();
      const available = result?.data?.node?.availableForSale ?? false;
      return available;
    } catch (error) {
      console.error('[ProductGrouping] Error fetching real-time variant availability:', error);
      return false;
    }
  }

  _notifyAmpBackInStock(variant) {
    try {
      // Method 1: Update the variant input that the app might be watching
      const variantInput = document.querySelector('input[name="id"]');
      if (variantInput && variant.id) {
        variantInput.value = variant.id;
        // Trigger change event to notify any listeners
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Method 2: Try common AMP Back in Stock methods if they exist
      if (window.AMP && window.AMP.BackInStock && typeof window.AMP.BackInStock.selectVariant === 'function') {
        window.AMP.BackInStock.selectVariant(variant.id);
      } else if (window.ampBackInStock && typeof window.ampBackInStock.selectVariant === 'function') {
        window.ampBackInStock.selectVariant(variant.id);
      }

      // Method 3: Dispatch custom event that the app might listen for
      window.dispatchEvent(new CustomEvent('amp:variant:change', {
        detail: {
          variant: variant,
          productId: variant.product_id || window.meta?.product?.id
        }
      }));

      // Method 4: Update any data attributes that the app might read
      const bisButtons = document.querySelectorAll('[data-bis-trigger]');
      bisButtons.forEach(button => {
        button.setAttribute('data-variant-id', variant.id);
        button.setAttribute('data-product-id', variant.product_id || window.meta?.product?.id || '');
      });

    } catch (error) {
      console.warn('[ProductGrouping] Error notifying AMP Back in Stock app:', error);
    }
  }

  async _handleOptionChange(event, option) {
    event.preventDefault();
    if (option.classList.contains('is-disabled')) return;

    const newProduct = option.dataset.productHandle;
    const loadedIds = this.dataProductGroup?.loaded_ids?.replace(/"/g, '') || false;

    if (newProduct && newProduct !== this.currentProductHandle) {
      try {
        this.parentComponent.classList.add("is-loading");

        let url = new URL(window.location.origin + `/products/${newProduct}`);
        let params = new URLSearchParams();
        params.set("view", "product-info-ajax");
        params.set("section_id", this.sectionId);
        params.set("no-fade-in", "true");
        if (loadedIds) {
          params.set("product-group-ids", loadedIds);
        }
        url.search = params.toString();
        const decodedURL = url.toString().replace(/%2B/g, "+");
        if (this.debug) console.log("decodedURL:", decodedURL);

        const response = await fetch(decodedURL);
        const text = await response.text();
        const parser = new DOMParser();
        const newDocument = parser.parseFromString(text, "text/html");

        const newMainProduct = newDocument.querySelector("#main-product-info");
        const currentMainProduct = this.isQuickView
          ? document.querySelector("quick-view-modal .quick-add-modal")
          : document.querySelector("#main-product-info");

        this.firstVariantId =
          newMainProduct?.getAttribute("data-first-variant");
        this.productId = newMainProduct?.getAttribute("data-product-id");
        this.currentProductHandle = newProduct;
        this.currentProductId = this.productId;
        this.currentVariantId = this.firstVariantId;

        if (newMainProduct && currentMainProduct) {
          this.isQuickView
            ? (currentMainProduct.innerHTML = newMainProduct.innerHTML)
            : currentMainProduct.replaceWith(newMainProduct);
        }

        // Re-bind variant buttons
        this._rebindVariantButtons();

        // Update product grouping data
        this.dataProductGroup = JSON.parse(
          this.querySelector("script[data-product-group]")?.textContent || "{}"
        );

        // ✅ Reset button state for the new product and check availability
        const newAddToCartForm = document.querySelector(
          ".product-info .shopify-product-form"
        );
        const newAddToCartButton = newAddToCartForm?.querySelector(
          'buy-buttons button[type="submit"]'
        );
        const newNotifyMeBtn = newAddToCartForm?.querySelector(
          "buy-buttons button[data-bis-trigger]"
        );
        const newAddToCartButtonText = newAddToCartButton?.querySelector("div");
        const newSoldOutElement =
          this.closest(".product-info")?.querySelector("sold-out-badge");

        if (
          this.firstVariantId &&
          newAddToCartButton &&
          newNotifyMeBtn &&
          newAddToCartButtonText
        ) {
          // Check availability of the new product's default variant
          this._fetchVariantAvailability(this.firstVariantId)
            .then((realTimeAvailable) => {
              console.log(
                `[ProductGrouping]: New product variant ${this.firstVariantId} available: ${realTimeAvailable}`
              );

              if (realTimeAvailable) {
                // Show Add to Bag for available variant
                const isPreorder = document
                  .querySelector("#main-product-info")
                  ?.classList.contains("preorder");
                newAddToCartButton.removeAttribute("disabled");
                newAddToCartButton.classList.remove("button--subdued");

                // Get variant price from the new product data
                const variantData = newMainProduct?.querySelector(
                  `[data-variant-id="${this.firstVariantId}"]`
                );
                const variantPrice = variantData?.dataset.variantPrice || "";
                const num =
                  parseFloat((variantPrice || "").replace(/[^0-9.]/g, "")) || 0;
                const formatted = num.toLocaleString(undefined, {
                  style: "currency",
                  currency: "USD",
                });

                newAddToCartButtonText.textContent = isPreorder
                  ? "PREORDER"
                  : `Add to Bag`;
                newAddToCartButton.style.display = "block";
                newNotifyMeBtn.style.display = "none";
                if (newSoldOutElement)
                  newSoldOutElement.classList.add("is-hidden");
              } else {
                // Show Join Waitlist for sold out variant
                this.showJoinWaitListButton(
                  newAddToCartButton,
                  newNotifyMeBtn,
                  newAddToCartButtonText,
                  newSoldOutElement
                );
              }
            })
            .catch((error) => {
              console.error(
                "[ProductGrouping] Failed to check new product variant availability:",
                error
              );
              // Fallback: assume available and let other checks handle it
              newAddToCartButton.removeAttribute("disabled");
              newAddToCartButton.classList.remove("button--subdued");
              newAddToCartButton.style.display = "block";
              newNotifyMeBtn.style.display = "none";
              if (newSoldOutElement)
                newSoldOutElement.classList.add("is-hidden");
            });
        }

        const scripts = Array.from(
          newMainProduct?.querySelectorAll("script") || []
        ).filter((script) => !script.hasAttribute("data-product-group"));
        scripts.forEach((oldScript) => {
          const newScript = document.createElement("script");
          newScript.textContent = oldScript.textContent;
          document.body.appendChild(newScript);
          document.body.removeChild(newScript);
        });

        window.dispatchEvent(new Event("productGroupingInjected"));

        if (!this.isQuickView) {
          params.delete("view");
          params.delete("section_id");
          params.delete("no-fade-in");
          if (loadedIds) {
            params.delete("product-group-ids");
          }
          url.search = params.toString();
          window.history.pushState({}, "", url.toString());

          const optionColor = document.querySelector(
            `button[data-product-handle="${newProduct}"]`
          );
          const currentQuickAdd = document.querySelector("#quick-add--sticky");
          const newQuickAdd = newDocument.querySelector("#quick-add--sticky");
          if (currentQuickAdd && newQuickAdd) {
            currentQuickAdd.replaceWith(newQuickAdd);
          }

          setTimeout(() => {
            if (!this.isQuickView) {
              window.yotpoWidgetsContainer?.initWidgets();
            }
            if (optionColor) {
              optionColor.focus();
            }
            const isMobile = window.matchMedia("(max-width: 768px)").matches;
            if (isMobile) {
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }, 300);
        }

        this.currentProductHandle = newProduct;
      } catch (error) {
        console.error('Error fetching new product data:', error);
      } finally {
        this.parentComponent.classList.remove('is-loading');
      }
    }
  }

  _setupSizeGuide() {
    this.sizeGuideButton = document.querySelector(".open-size-guide");
    this.sizeGuideModal = document.querySelector(".size-guide-modal");
    this.closeButton = document.querySelector(".modal-close");
    this.modalContent = document.querySelector(".modal-content");

    if (this.sizeGuideButton) {
      this.sizeGuideButton.addEventListener("click", () => this.openSizeGuideModal());
    }

    if (this.closeButton) {
      this.closeButton.addEventListener("click", () => this.closeSizeGuideModal());
    }

    if (this.sizeGuideModal) {
      this.sizeGuideModal.addEventListener("click", (event) => {
        if (event.target === this.sizeGuideModal) {
          this.closeSizeGuideModal();
        }
      });
    }

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.sizeGuideModal?.classList.contains("active")) {
        this.closeSizeGuideModal();
      }
    });

    this.tabButtons = document.querySelectorAll(".size-guide-tab-buttons .tab-button");
    this.tabPanels = document.querySelectorAll(".size-guide-tab-content .tab-panel");

    this.tabButtons.forEach((button) => {
      button.addEventListener("click", (event) => this.switchTab(event));
    });

    window.addEventListener("productGroupingInjected", () => this._updateSizeGuide());
  }

  switchTab(event) {
    const selectedTab = event.target.getAttribute("data-tab");
    this.tabButtons.forEach((btn) => btn.classList.remove("active"));
    this.tabPanels.forEach((panel) => panel.classList.remove("active"));
    event.target.classList.add("active");
    document.getElementById(selectedTab)?.classList.add("active");
  }

  openSizeGuideModal() {
    this.sizeGuideModal?.classList.add("active");
    document.body.classList.add('modal-open');
    this._generateSizeChartTabs();
  }

  closeSizeGuideModal() {
    this.sizeGuideModal?.classList.remove('active');
    document.body.classList.remove('modal-open');
  }

  _generateSizeChartTabs() {
    const sizeChartContent = document.querySelector('#size-chart-content');
    if (!sizeChartContent) return;

    const tables = sizeChartContent.querySelectorAll("table");
    const paragraphs = sizeChartContent.querySelectorAll("p");

    if (tables.length === 0) return;

    let subTabsHTML = `<div class="size-guide-sub-tabs" role="tablist">`;
    let subContentHTML = `<div class="size-guide-sub-content">`;

    tables.forEach((table, index) => {
      let label = `Size Chart ${index + 1}`;
      for (let i = paragraphs.length - 1; i >= 0; i--) {
        if (paragraphs[i].nextElementSibling === table && paragraphs[i].textContent.trim() !== "") {
          label = paragraphs[i].textContent.trim();
          break;
        }
      }
      subTabsHTML += `<button class="sub-tab-button ${index === 0 ? 'active' : ''}" data-tab="sub-tab-${index}" role="tab" aria-controls="sub-tab-${index}" aria-selected="${index === 0 ? 'true' : 'false'}">${label}</button>`;
      subContentHTML += `<div class="sub-tab-panel ${index === 0 ? 'active' : ''}" id="sub-tab-${index}" role="tabpanel">${table.outerHTML}</div>`;
    });

    subTabsHTML += `</div>`;
    subContentHTML += `</div>`;

    sizeChartContent.innerHTML = subTabsHTML + subContentHTML;

    document.querySelectorAll(".sub-tab-button").forEach(button => {
      button.addEventListener("click", function() {
        const tab = this.getAttribute("data-tab");
        document.querySelectorAll(".sub-tab-button").forEach(btn => btn.classList.remove("active"));
        document.querySelectorAll(".sub-tab-panel").forEach(panel => panel.classList.remove("active"));
        this.classList.add("active");
        document.getElementById(tab)?.classList.add("active");
      });
    });
  }

  _updateSizeGuide() {
    const productTags = this.getAttribute("data-product-tags") || "";
    const sizeGuideHandle = this._extractSizeGuideTag(productTags);

    if (sizeGuideHandle) {
      fetch(`/pages/${sizeGuideHandle}`)
        .then(response => response.text())
        .then(sizeChart => {
          document.querySelector("#size-chart-content").innerHTML = sizeChart;
        })
        .catch(error => console.error("Error loading size guide:", error));
    }
  }

  _extractSizeGuideTag(tags) {
    const match = tags.split(",").find(tag => tag.includes("size-guide:"));
    return match ? match.split(":")[1] : "";
  }
}

customElements.define('product-grouping', ProductGrouping);